/**
 * ITER308 · Phase A — Maintenance Module Hub.
 *
 * The Maintenance module now covers three surfaces:
 *   • Assets      — the equipment register (NEW, primary tab).
 *   • Utilities   — meter readings, budgets, expenses (existing ITER
 *                    module content, preserved verbatim under this tab).
 *   • Categories  — per-tenant asset category master.
 *
 * The existing utility-tracking UI is imported wholesale so historical
 * consumption records render identically pre-/post-rename.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, Wrench, Boxes, Layers, ChevronRight, Pencil, Trash2, Zap, LayoutDashboard, ClipboardCheck, ClipboardList, Copy, AlertCircle, GripVertical, CalendarClock, Power, PowerOff } from 'lucide-react';
import UtilitiesModule from './AdminMaintenanceUtilities';
import MaintenanceDashboardTab from './MaintenanceDashboardTab';
import { AdminDailyReadingsSetup } from './DailyReadings';

const HEALTH_STYLES = {
  healthy:          { label: 'Healthy',         cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  needs_attention:  { label: 'Needs Attention', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  critical:         { label: 'Critical',        cls: 'bg-red-50 text-red-800 border-red-200' },
  out_of_service:   { label: 'Out of Service',  cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

// ITER308 · Airline-standard heading band used across every section.
function HeadingBand({ title, subtitle, action }) {
  return (
    <div className="rounded-lg mb-4 overflow-hidden border border-[color:var(--color-primary)]/30 shadow-sm">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5"
           style={{ background: 'var(--color-primary)', color: 'white' }}>
        <div>
          <div className="text-sm font-semibold tracking-wide">{title}</div>
          {subtitle && <div className="text-[11px] opacity-80">{subtitle}</div>}
        </div>
        {action}
      </div>
    </div>
  );
}

export default function AdminMaintenance() {
  return (
    <div data-testid="admin-maintenance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-[#1A2421]">Maintenance</h2>
          <p className="text-sm text-[#5C6B64] mt-1">
            Asset register, utility consumption, and equipment categories.
          </p>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-[#F3F5F2] mb-6">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-dashboard">
            <LayoutDashboard className="w-4 h-4 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="assets" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-assets">
            <Boxes className="w-4 h-4 mr-1.5" /> Assets
          </TabsTrigger>
          <TabsTrigger value="utilities" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-utilities">
            <Zap className="w-4 h-4 mr-1.5" /> Utilities
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-categories">
            <Layers className="w-4 h-4 mr-1.5" /> Categories
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-templates">
            <ClipboardList className="w-4 h-4 mr-1.5" /> Inspection Templates
          </TabsTrigger>
          <TabsTrigger value="schedules" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-schedules">
            <CalendarClock className="w-4 h-4 mr-1.5" /> PM Schedules
          </TabsTrigger>
          <TabsTrigger value="readings" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtn-tab-readings-setup">
            <ClipboardCheck className="w-4 h-4 mr-1.5" /> Daily Readings Setup
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><MaintenanceDashboardTab /></TabsContent>
        <TabsContent value="assets"><AssetsTab /></TabsContent>
        <TabsContent value="utilities"><UtilitiesModule /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="schedules"><PMSchedulesTab /></TabsContent>
        <TabsContent value="readings"><AdminDailyReadingsSetup /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Assets Tab
// ─────────────────────────────────────────────────────────────────────
function AssetsTab() {
  const { getHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [outletFilter, setOutletFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, oRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/maintenance/assets`, { headers: getHeaders() }),
        // ITER308-B · Use the tenant-scoped /outlets endpoint (not the
        // admin-only /admin/outlets) so scoped-admin roles like
        // maintenance_manager can see their assigned outlets.
        axios.get(`${API}/outlets`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/categories`, { headers: getHeaders() }),
      ]);
      setAssets(aRes.data);
      setOutlets(oRes.data.filter((o) => o.status !== 'inactive'));
      setCategories(cRes.data);
    } catch (err) {
      toast.error('Failed to load assets');
    } finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm(),
              outlet_id:   outlets[0]?.id || '',
              category_id: categories[0]?.id || '' });
    setDialogOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      outlet_id: a.outlet_id, category_id: a.category_id, name: a.name,
      area_code: a.area_code, brand: a.brand || '', model: a.model || '',
      serial_number: a.serial_number || '', installation_date: a.installation_date || '',
      warranty_expiry: a.warranty_expiry || '', amc_provider: a.amc_provider || '',
      amc_expiry: a.amc_expiry || '', vendor: a.vendor || '',
      purchase_date: a.purchase_date || '',
      purchase_cost: a.purchase_cost != null ? String(a.purchase_cost) : '',
      location: a.location || '', remarks: a.remarks || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.category_id || !form.outlet_id) {
      toast.error('Outlet, category, and name are required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form,
        purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null };
      if (editing) {
        await axios.put(`${API}/admin/maintenance/assets/${editing.id}`, payload,
                         { headers: getHeaders() });
        toast.success('Asset updated');
      } else {
        await axios.post(`${API}/admin/maintenance/assets`, payload,
                          { headers: getHeaders() });
        toast.success('Asset registered');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const decommission = async (a) => {
    if (!window.confirm(`Decommission asset ${a.asset_tag}? Its service history stays.`))
      return;
    try {
      await axios.delete(`${API}/admin/maintenance/assets/${a.id}`,
                          { headers: getHeaders() });
      toast.success('Asset decommissioned');
      load();
    } catch { toast.error('Failed'); }
  };

  const filtered = outletFilter === 'all'
    ? assets
    : assets.filter((a) => a.outlet_id === outletFilter);

  return (
    <div>
      <HeadingBand
        title="Equipment Register"
        subtitle={`${assets.length} asset${assets.length === 1 ? '' : 's'} across ${outlets.length} outlet${outlets.length === 1 ? '' : 's'}`}
        action={
          <div className="flex items-center gap-2">
            <Select value={outletFilter} onValueChange={setOutletFilter}>
              <SelectTrigger className="h-8 w-40 bg-white/10 border-white/20 text-white text-xs"
                              data-testid="asset-outlet-filter">
                <SelectValue placeholder="Outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outlets</SelectItem>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={openAdd}
                    className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                    data-testid="asset-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Asset
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="text-sm text-[#5C6B64] py-12 text-center">Loading assets…</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="py-14 text-center">
            <Wrench className="w-8 h-8 text-[#5C6B64] mx-auto mb-3 opacity-60" />
            <div className="text-sm font-medium text-[#1A2421]">No assets registered yet</div>
            <div className="text-xs text-[#5C6B64] mt-1 max-w-md mx-auto">
              Add your first piece of equipment — its full service history and preventive-maintenance
              schedule will start collecting here.
            </div>
            <Button size="sm" onClick={openAdd} className="mt-4 bg-[color:var(--color-primary)] text-white">
              <Plus className="w-3.5 h-3.5 mr-1" /> Register asset
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                  <th className="px-4 py-2.5">Asset Tag</th>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Category</th>
                  <th className="px-4 py-2.5">Outlet · Location</th>
                  <th className="px-4 py-2.5">Health</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => {
                  const h = HEALTH_STYLES[a.health] || HEALTH_STYLES.healthy;
                  return (
                    <tr key={a.id}
                        className={`${i > 0 ? 'border-t border-[#E2E8DF]' : ''} hover:bg-[#F9F9F8] cursor-pointer`}
                        onClick={() => navigate(`/admin/maintenance/assets/${a.id}`)}
                        data-testid={`asset-row-${a.id}`}>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-[color:var(--color-primary)] font-semibold"
                          data-testid={`asset-tag-${a.id}`}>
                        {a.asset_tag}
                      </td>
                      <td className="px-4 py-2.5 text-[#1A2421]">{a.name}</td>
                      <td className="px-4 py-2.5 text-[#5C6B64]">{a.category_name}</td>
                      <td className="px-4 py-2.5 text-[#5C6B64]">
                        <div className="truncate max-w-[240px]">{a.outlet_name}</div>
                        <div className="text-[11px] opacity-80 truncate max-w-[240px]">{a.location || '—'}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge className={`text-[10px] px-1.5 py-0.5 border ${h.cls}`}
                               data-testid={`asset-health-${a.id}`}>{h.label}</Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline"
                               className={`text-[10px] px-1.5 py-0.5 ${
                                 a.status === 'active' ? 'text-emerald-800 border-emerald-300' :
                                 a.status === 'inactive' ? 'text-amber-800 border-amber-300' :
                                 'text-slate-600 border-slate-300'}`}>
                          {a.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(a)}
                                  className="text-[#5C6B64] h-7 w-7 p-0"
                                  data-testid={`asset-edit-${a.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => decommission(a)}
                                  className="text-[#C45A41] h-7 w-7 p-0"
                                  data-testid={`asset-decom-${a.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          <ChevronRight className="w-3.5 h-3.5 text-[#5C6B64] opacity-40" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <AssetDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        editing={editing} form={form} setForm={setForm}
        outlets={outlets} categories={categories}
        saving={saving} onSave={save} />
    </div>
  );
}

function emptyForm() {
  return {
    outlet_id: '', category_id: '', name: '', area_code: 'KIT',
    brand: '', model: '', serial_number: '',
    installation_date: '', warranty_expiry: '', amc_provider: '', amc_expiry: '',
    vendor: '', purchase_date: '', purchase_cost: '',
    location: '', remarks: '',
  };
}

function AssetDialog({ open, onOpenChange, editing, form, setForm, outlets, categories, saving, onSave }) {
  const F = (k) => form[k];
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="asset-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A2421]">{editing ? `Edit Asset · ${editing.asset_tag}` : 'Register Asset'}</DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {editing
              ? 'The asset tag is immutable once minted — printed labels remain valid.'
              : 'A unique tag will be auto-generated in the format OUTLET-AREA-CATEGORY-SEQ.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Outlet *">
            <Select value={F('outlet_id')} onValueChange={(v) => set('outlet_id', v)} disabled={!!editing}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="asset-outlet-select"><SelectValue placeholder="Select outlet" /></SelectTrigger>
              <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Category *">
            <Select value={F('category_id')} onValueChange={(v) => set('category_id', v)}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="asset-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Asset Name *" full>
            <Input value={F('name')} onChange={(e) => set('name', e.target.value)}
                    placeholder="e.g. Cold-storage deep freezer"
                    className="h-10 border-[#E2E8DF]" data-testid="asset-name-input" />
          </Field>
          <Field label="Area Code" hint="Short code (KIT, BAR, HK, RST, GEN)">
            <Input value={F('area_code')} onChange={(e) => set('area_code', e.target.value.toUpperCase().slice(0, 4))}
                    className="h-10 border-[#E2E8DF] font-mono uppercase" data-testid="asset-area-input" />
          </Field>
          <Field label="Location">
            <Input value={F('location')} onChange={(e) => set('location', e.target.value)}
                    placeholder="e.g. Kitchen — Cold Storage"
                    className="h-10 border-[#E2E8DF]" />
          </Field>
          <Field label="Brand"><Input value={F('brand')} onChange={(e) => set('brand', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="Model"><Input value={F('model')} onChange={(e) => set('model', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="Serial Number" full>
            <Input value={F('serial_number')} onChange={(e) => set('serial_number', e.target.value)}
                    className="h-10 border-[#E2E8DF] font-mono" />
          </Field>
          <Field label="Installation Date"><Input type="date" value={F('installation_date') || ''} onChange={(e) => set('installation_date', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="Warranty Expiry"><Input type="date" value={F('warranty_expiry') || ''} onChange={(e) => set('warranty_expiry', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="AMC Provider"><Input value={F('amc_provider')} onChange={(e) => set('amc_provider', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="AMC Expiry"><Input type="date" value={F('amc_expiry') || ''} onChange={(e) => set('amc_expiry', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="Vendor"><Input value={F('vendor')} onChange={(e) => set('vendor', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="Purchase Date"><Input type="date" value={F('purchase_date') || ''} onChange={(e) => set('purchase_date', e.target.value)} className="h-10 border-[#E2E8DF]" /></Field>
          <Field label="Purchase Cost (₹)" full>
            <Input type="number" value={F('purchase_cost')} onChange={(e) => set('purchase_cost', e.target.value)}
                    className="h-10 border-[#E2E8DF]" />
          </Field>
          <Field label="Remarks" full>
            <Input value={F('remarks')} onChange={(e) => set('remarks', e.target.value)}
                    className="h-10 border-[#E2E8DF]" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="border-[#E2E8DF]">Cancel</Button>
          <Button onClick={onSave} disabled={saving}
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                  data-testid="asset-save-btn">
            {saving ? 'Saving…' : editing ? 'Update' : 'Register'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, full, children }) {
  return (
    <div className={`space-y-1 ${full ? 'col-span-2' : ''}`}>
      <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">{label}</Label>
      {children}
      {hint && <div className="text-[10px] text-[#5C6B64] opacity-80">{hint}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Categories Tab
// ─────────────────────────────────────────────────────────────────────
function CategoriesTab() {
  const { getHeaders, API } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/maintenance/categories`, { headers: getHeaders() });
      setItems(res.data);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/maintenance/categories`,
                        { name, code_prefix: prefix }, { headers: getHeaders() });
      toast.success('Category added');
      setName(''); setPrefix(''); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/maintenance/categories/${c.id}`,
                          { headers: getHeaders() });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div>
      <HeadingBand title="Equipment Categories"
                    subtitle={`${items.length} categor${items.length === 1 ? 'y' : 'ies'}`} />
      <Card className="border-[#E2E8DF] shadow-sm mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2 items-end">
            <Field label="New category name">
              <Input value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Water Heater"
                      className="h-10 border-[#E2E8DF]" data-testid="cat-name-input" />
            </Field>
            <Field label="Code Prefix (optional)">
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase().slice(0, 4))}
                      placeholder="Auto"
                      className="h-10 border-[#E2E8DF] font-mono uppercase" />
            </Field>
            <Button onClick={add} disabled={saving}
                    className="h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="cat-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-[#5C6B64]">Loading…</div> :
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F8]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Code Prefix</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c, i) => (
                <tr key={c.id} className={i > 0 ? 'border-t border-[#E2E8DF]' : ''}>
                  <td className="px-4 py-2.5 text-[#1A2421]">{c.name}</td>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-[color:var(--color-primary)] font-semibold">{c.code_prefix}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(c)}
                            className="text-[#C45A41] h-7 w-7 p-0"
                            data-testid={`cat-del-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>}
        </CardContent>
      </Card>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
//  Inspection Templates Tab · ITER-mtn-pass1
//  Reuses the existing CRUD in backend/routes/maintenance_ops.py:
//    GET/POST/PUT/DELETE /admin/maintenance/inspection-templates[/{id}]
//    GET  /admin/maintenance/inspection-templates/schedule-counts
//  Shared editor drives Create + Edit + Duplicate.
// ─────────────────────────────────────────────────────────────────────
const KIND_OPTIONS = [
  { value: 'pass_fail',         label: 'Pass / Fail' },
  { value: 'numeric',           label: 'Numeric' },
  { value: 'pass_fail_numeric', label: 'Pass / Fail + Numeric' },
];

function emptyTemplate() {
  return { category_id: '', name: '', description: '', items: [] };
}
function emptyItem() {
  return {
    key: `it_${Math.random().toString(36).slice(2, 8)}`,
    label: '',
    kind: 'pass_fail',
    unit: '',
    min_value: null,
    max_value: null,
    is_critical: false,
    failure_assign_to: null,
  };
}

function TemplatesTab() {
  const { getHeaders, API } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [scheduleCounts, setScheduleCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // template id or null (create/duplicate)
  const [form, setForm] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, cntRes] = await Promise.all([
        axios.get(`${API}/admin/maintenance/inspection-templates`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/categories`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/inspection-templates/schedule-counts`, { headers: getHeaders() }),
      ]);
      setTemplates(tRes.data);
      setCategories(cRes.data);
      setScheduleCounts(cntRes.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load templates');
    } finally { setLoading(false); }
  }, [API, getHeaders]);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || '—';

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyTemplate(), category_id: categories[0]?.id || '' });
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      category_id: t.category_id || '',
      name: t.name || '',
      description: t.description || '',
      items: (t.items || []).map((it) => ({
        key: it.key || `it_${Math.random().toString(36).slice(2, 8)}`,
        label: it.label || '',
        kind: it.kind || 'pass_fail',
        unit: it.unit || '',
        min_value: it.min_value ?? null,
        max_value: it.max_value ?? null,
        is_critical: !!it.is_critical,
        failure_assign_to: it.failure_assign_to || null,
      })),
    });
    setDialogOpen(true);
  };
  const openDuplicate = (t) => {
    setEditing(null);
    setForm({
      category_id: t.category_id || '',
      name: `${t.name} (Copy)`,
      description: t.description || '',
      items: (t.items || []).map((it) => ({ ...it,
        key: `it_${Math.random().toString(36).slice(2, 8)}` })),
    });
    setDialogOpen(true);
  };

  const remove = async (t) => {
    const refs = scheduleCounts[t.id] || 0;
    const msg = refs > 0
      ? `Delete "${t.name}"?\n\nThis template is referenced by ${refs} active PM schedule(s). The server will refuse deletion until they are reassigned or deactivated.`
      : `Delete inspection template "${t.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/admin/maintenance/inspection-templates/${t.id}`,
        { headers: getHeaders() });
      toast.success('Template deleted');
      load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Failed to delete template';
      if (status === 409) toast.error(detail, { duration: 6000 });
      else toast.error(detail);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    if (!form.category_id) { toast.error('Category is required'); return; }
    if (!form.items.length) { toast.error('Add at least one inspection item'); return; }
    for (const it of form.items) {
      if (!it.label.trim()) { toast.error('Every inspection item needs a name'); return; }
    }
    // Normalise numeric fields — send null when not applicable.
    const payload = {
      category_id: form.category_id,
      name: form.name.trim(),
      description: form.description || '',
      items: form.items.map((it) => {
        const isNumeric = it.kind === 'numeric' || it.kind === 'pass_fail_numeric';
        return {
          key: it.key,
          label: it.label.trim(),
          kind: it.kind,
          unit: isNumeric ? (it.unit || null) : null,
          min_value: isNumeric && it.min_value !== '' && it.min_value != null ? Number(it.min_value) : null,
          max_value: isNumeric && it.max_value !== '' && it.max_value != null ? Number(it.max_value) : null,
          is_critical: !!it.is_critical,
          failure_assign_to: it.failure_assign_to || null,
        };
      }),
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/maintenance/inspection-templates/${editing}`,
          payload, { headers: getHeaders() });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/admin/maintenance/inspection-templates`,
          payload, { headers: getHeaders() });
        toast.success('Template created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="templates-tab">
      <HeadingBand
        title="Inspection Templates"
        subtitle={`${templates.length} template${templates.length === 1 ? '' : 's'} · reused by PM schedules`}
        action={
          <Button onClick={openCreate} size="sm"
            className="bg-white text-[color:var(--color-primary)] hover:bg-white/90 h-8"
            data-testid="tpl-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Create Inspection Template
          </Button>
        }
      />

      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-[#5C6B64]">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-8 h-8 text-[#5C6B64] mx-auto mb-2 opacity-60" />
              <div className="text-sm font-medium text-[#1A2421]">No inspection templates yet</div>
              <div className="text-xs text-[#5C6B64] mt-1">
                Create a template to standardise preventive-maintenance checks across assets.
              </div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="tpl-list-table">
              <thead className="bg-[#F9F9F8]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                  <th className="px-4 py-2.5">Template Name</th>
                  <th className="px-4 py-2.5">Category</th>
                  <th className="px-4 py-2.5 text-right">Items</th>
                  <th className="px-4 py-2.5 text-right">Active PM Schedules</th>
                  <th className="px-4 py-2.5">Last Updated</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, i) => {
                  const refs = scheduleCounts[t.id] || 0;
                  return (
                    <tr key={t.id}
                        className={i > 0 ? 'border-t border-[#E2E8DF]' : ''}
                        data-testid={`tpl-row-${t.id}`}>
                      <td className="px-4 py-2.5 text-[#1A2421] font-medium">{t.name}</td>
                      <td className="px-4 py-2.5 text-[#5C6B64]">{catName(t.category_id)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{(t.items || []).length}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`tpl-refs-${t.id}`}>
                        {refs > 0 ? (
                          <Badge className="bg-amber-50 text-amber-800 border-amber-200">{refs}</Badge>
                        ) : (
                          <span className="text-[#5C6B64]">0</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-[#5C6B64] text-[12px]">
                        {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[color:var(--color-primary)]"
                          title="Edit" onClick={() => openEdit(t)}
                          data-testid={`tpl-edit-${t.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#5C6B64]"
                          title="Duplicate" onClick={() => openDuplicate(t)}
                          data-testid={`tpl-dup-${t.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#C45A41]"
                          title="Delete" onClick={() => remove(t)}
                          data-testid={`tpl-del-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <TemplateEditorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        categories={categories}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}

function TemplateEditorDialog({ open, onOpenChange, editing, form, setForm, categories, onSave, saving }) {
  const patchItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));
  };
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const moveItem = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.items]; const to = idx + dir;
      if (to < 0 || to >= arr.length) return f;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return { ...f, items: arr };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tpl-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Inspection Template' : 'Create Inspection Template'}</DialogTitle>
          <DialogDescription>
            Templates are reused by preventive-maintenance schedules. Existing PM tasks continue to use the version captured at task-generation time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Template Name">
              <Input value={form.name}
                     onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     placeholder="Monthly Kitchen Equipment Inspection"
                     className="h-10 border-[#E2E8DF]"
                     data-testid="tpl-name-input" />
            </Field>
            <Field label="Category">
              <Select value={form.category_id}
                      onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="tpl-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description (optional)" full>
              <Input value={form.description}
                     onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                     placeholder="Purpose or scope of this inspection"
                     className="h-10 border-[#E2E8DF]"
                     data-testid="tpl-desc-input" />
            </Field>
          </div>

          <div className="border-t border-[#E2E8DF] pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
                Inspection Items · {form.items.length}
              </div>
              <Button size="sm" variant="outline" onClick={addItem}
                className="h-8 border-[color:var(--color-primary)] text-[color:var(--color-primary)]"
                data-testid="tpl-add-item">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add inspection item
              </Button>
            </div>

            {form.items.length === 0 ? (
              <div className="text-[12px] text-[#5C6B64] italic py-3 text-center border border-dashed border-[#E2E8DF] rounded">
                No items yet — add at least one check to save this template.
              </div>
            ) : (
              <div className="space-y-2">
                {form.items.map((it, idx) => {
                  const isNumeric = it.kind === 'numeric' || it.kind === 'pass_fail_numeric';
                  return (
                    <div key={it.key}
                         className="border border-[#E2E8DF] rounded-md p-3 bg-[#FAFCFA]"
                         data-testid={`tpl-item-${idx}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col gap-0.5 pt-1.5">
                          <button type="button" onClick={() => moveItem(idx, -1)}
                            className="text-[#5C6B64] hover:text-[color:var(--color-primary)] disabled:opacity-30"
                            disabled={idx === 0} title="Move up" data-testid={`tpl-item-up-${idx}`}>
                            <GripVertical className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-[9px] text-[#5C6B64] text-center font-mono">{idx + 1}</span>
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_150px] gap-2">
                          <Input value={it.label}
                                 onChange={(e) => patchItem(idx, { label: e.target.value })}
                                 placeholder="Check name (e.g. External condition)"
                                 className="h-9 border-[#E2E8DF]"
                                 data-testid={`tpl-item-label-${idx}`} />
                          <Select value={it.kind}
                                  onValueChange={(v) => patchItem(idx, { kind: v })}>
                            <SelectTrigger className="h-9 border-[#E2E8DF]" data-testid={`tpl-item-kind-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {KIND_OPTIONS.map((k) => (
                                <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button size="sm" variant="ghost"
                          className="h-8 w-8 p-0 text-[#C45A41]" onClick={() => removeItem(idx)}
                          data-testid={`tpl-item-remove-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>

                      {isNumeric && (
                        <div className="grid grid-cols-3 gap-2 mt-2 pl-7">
                          <div>
                            <Label className="text-[10px] uppercase text-[#5C6B64] tracking-wider">Unit</Label>
                            <Input value={it.unit || ''}
                                   onChange={(e) => patchItem(idx, { unit: e.target.value })}
                                   placeholder="°C, PSI, %"
                                   className="h-8 border-[#E2E8DF] text-sm mt-0.5"
                                   data-testid={`tpl-item-unit-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-[#5C6B64] tracking-wider">Min</Label>
                            <Input type="number" value={it.min_value ?? ''}
                                   onChange={(e) => patchItem(idx, { min_value: e.target.value === '' ? null : e.target.value })}
                                   placeholder="—"
                                   className="h-8 border-[#E2E8DF] text-sm mt-0.5"
                                   data-testid={`tpl-item-min-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-[#5C6B64] tracking-wider">Max</Label>
                            <Input type="number" value={it.max_value ?? ''}
                                   onChange={(e) => patchItem(idx, { max_value: e.target.value === '' ? null : e.target.value })}
                                   placeholder="—"
                                   className="h-8 border-[#E2E8DF] text-sm mt-0.5"
                                   data-testid={`tpl-item-max-${idx}`} />
                          </div>
                        </div>
                      )}

                      <div className="mt-2 pl-7 flex items-center gap-4">
                        <label className="flex items-center gap-1.5 text-[12px] text-[#1A2421] cursor-pointer">
                          <input type="checkbox" checked={!!it.is_critical}
                            onChange={(e) => patchItem(idx, { is_critical: e.target.checked })}
                            data-testid={`tpl-item-critical-${idx}`} />
                          <AlertCircle className="w-3.5 h-3.5 text-[#C45A41]" />
                          Critical — failure raises a ticket
                        </label>
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <Input value={it.failure_assign_to?.role || ''}
                            onChange={(e) => patchItem(idx, {
                              failure_assign_to: {
                                ...(it.failure_assign_to || {}),
                                role: e.target.value,
                              },
                            })}
                            placeholder="Failure → role (e.g. maintenance)"
                            className="h-8 border-[#E2E8DF] text-sm"
                            data-testid={`tpl-item-frole-${idx}`} />
                          <Input value={it.failure_assign_to?.user_id || ''}
                            onChange={(e) => patchItem(idx, {
                              failure_assign_to: {
                                ...(it.failure_assign_to || {}),
                                user_id: e.target.value,
                              },
                            })}
                            placeholder="Failure → user id (optional)"
                            className="h-8 border-[#E2E8DF] text-sm"
                            data-testid={`tpl-item-fuser-${idx}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tpl-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="tpl-save-btn">
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  PM Schedules Tab · ITER-mtn-pass2
//  Reuses existing PM schedule CRUD:
//    GET    /admin/maintenance/pm-schedules              (portfolio list)
//    POST   /admin/maintenance/assets/{asset_id}/pm-schedules
//    PATCH  /admin/maintenance/pm-schedules/{schedule_id}
//    DELETE /admin/maintenance/pm-schedules/{schedule_id}
//  Frequency + fields mirror the backend PMSchedule model exactly.
//  `next_due` is server-computed and displayed as-is — no frontend arithmetic.
// ─────────────────────────────────────────────────────────────────────
const FREQUENCY_OPTIONS = [
  { value: 'daily',        label: 'Daily' },
  { value: 'weekly',       label: 'Weekly' },
  { value: 'monthly',      label: 'Monthly' },
  { value: 'quarterly',    label: 'Quarterly' },
  { value: 'half_yearly',  label: 'Half-yearly' },
  { value: 'yearly',       label: 'Yearly' },
  { value: 'custom',       label: 'Custom' },
];
const ASSIGNEE_ROLE_OPTIONS = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'manager',     label: 'Manager' },
  { value: 'chef',        label: 'Chef' },
  { value: 'admin',       label: 'Admin' },
];
const FREQ_LABEL = Object.fromEntries(FREQUENCY_OPTIONS.map((f) => [f.value, f.label]));
const ROLE_LABEL = Object.fromEntries(ASSIGNEE_ROLE_OPTIONS.map((r) => [r.value, r.label]));

const todayISO = () => new Date().toISOString().slice(0, 10);
function emptySchedule() {
  return {
    asset_id: '',
    frequency: 'monthly',
    interval_days: null,
    inspection_template_id: null,
    description: '',
    default_assignee_role: 'maintenance',
    lead_days: 3,
    active: true,
    starts_on: todayISO(),
  };
}

function PMSchedulesTab() {
  const { getHeaders, API } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySchedule());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, aRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/maintenance/pm-schedules`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/assets`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/inspection-templates`, { headers: getHeaders() }),
      ]);
      setSchedules(sRes.data);
      setAssets(aRes.data);
      setTemplates(tRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load schedules');
    } finally { setLoading(false); }
  }, [API, getHeaders]);
  useEffect(() => { load(); }, [load]);

  const assetName = (id) => assets.find((a) => a.id === id)?.name || '—';
  const tplName   = (id) => templates.find((t) => t.id === id)?.name || (id ? '—' : 'No template');
  const assetCategoryId = (id) => assets.find((a) => a.id === id)?.category_id;
  const templatesForAsset = (assetId) => {
    const catId = assetCategoryId(assetId);
    if (!catId) return templates;
    return templates.filter((t) => t.category_id === catId);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptySchedule(), asset_id: assets[0]?.id || '' });
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      asset_id: s.asset_id,
      frequency: s.frequency || 'monthly',
      interval_days: s.interval_days ?? null,
      inspection_template_id: s.inspection_template_id || null,
      description: s.description || '',
      default_assignee_role: s.default_assignee_role || 'maintenance',
      lead_days: s.lead_days ?? 3,
      active: s.active !== false,
      starts_on: s.starts_on || todayISO(),
    });
    setDialogOpen(true);
  };

  const toggleActive = async (s) => {
    try {
      await axios.patch(`${API}/admin/maintenance/pm-schedules/${s.id}`, {
        asset_id: s.asset_id,
        frequency: s.frequency,
        interval_days: s.interval_days,
        inspection_template_id: s.inspection_template_id,
        description: s.description || '',
        default_assignee_role: s.default_assignee_role,
        lead_days: s.lead_days,
        active: !s.active,
        starts_on: s.starts_on,
      }, { headers: getHeaders() });
      toast.success(!s.active ? 'Schedule activated' : 'Schedule deactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update schedule');
    }
  };

  const remove = async (s) => {
    const asset = assetName(s.asset_id);
    if (!window.confirm(`Delete PM schedule for "${asset}"?\n\nHistorical PM tasks and inspections remain in place. Only the future schedule definition is removed.`)) return;
    try {
      await axios.delete(`${API}/admin/maintenance/pm-schedules/${s.id}`,
        { headers: getHeaders() });
      toast.success('Schedule deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const save = async () => {
    if (!form.asset_id) { toast.error('Asset is required'); return; }
    if (!form.frequency) { toast.error('Frequency is required'); return; }
    if (form.frequency === 'custom' && (!form.interval_days || Number(form.interval_days) < 1)) {
      toast.error('Custom frequency needs an interval in days'); return;
    }
    const payload = {
      asset_id: form.asset_id,
      frequency: form.frequency,
      interval_days: form.frequency === 'custom' ? Number(form.interval_days) : null,
      inspection_template_id: form.inspection_template_id || null,
      description: form.description || '',
      default_assignee_role: form.default_assignee_role || 'maintenance',
      lead_days: Number(form.lead_days ?? 3),
      active: !!form.active,
      starts_on: form.starts_on || todayISO(),
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/admin/maintenance/pm-schedules/${editing}`,
          payload, { headers: getHeaders() });
        toast.success('Schedule updated');
      } else {
        await axios.post(`${API}/admin/maintenance/assets/${form.asset_id}/pm-schedules`,
          payload, { headers: getHeaders() });
        toast.success('Schedule created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="schedules-tab">
      <HeadingBand
        title="Preventive-Maintenance Schedules"
        subtitle={`${schedules.length} schedule${schedules.length === 1 ? '' : 's'} · ${schedules.filter((s) => s.active).length} active`}
        action={
          <Button onClick={openCreate} size="sm" disabled={assets.length === 0}
            className="bg-white text-[color:var(--color-primary)] hover:bg-white/90 h-8"
            data-testid="sched-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Create PM Schedule
          </Button>
        }
      />

      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-[#5C6B64]">Loading…</div>
          ) : schedules.length === 0 ? (
            <div className="p-8 text-center">
              <CalendarClock className="w-8 h-8 text-[#5C6B64] mx-auto mb-2 opacity-60" />
              <div className="text-sm font-medium text-[#1A2421]">No PM schedules yet</div>
              <div className="text-xs text-[#5C6B64] mt-1">
                Create a schedule to auto-generate preventive-maintenance tasks for an asset.
              </div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="sched-list-table">
              <thead className="bg-[#F9F9F8]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                  <th className="px-4 py-2.5">Asset</th>
                  <th className="px-4 py-2.5">Template</th>
                  <th className="px-4 py-2.5">Frequency</th>
                  <th className="px-4 py-2.5">Next Due</th>
                  <th className="px-4 py-2.5">Start Date</th>
                  <th className="px-4 py-2.5 text-right">Lead Days</th>
                  <th className="px-4 py-2.5">Assignee</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s, i) => (
                  <tr key={s.id} className={i > 0 ? 'border-t border-[#E2E8DF]' : ''}
                      data-testid={`sched-row-${s.id}`}>
                    <td className="px-4 py-2.5 text-[#1A2421] font-medium">{assetName(s.asset_id)}</td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">{tplName(s.inspection_template_id)}</td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">
                      {FREQ_LABEL[s.frequency] || s.frequency}
                      {s.frequency === 'custom' && s.interval_days ? (
                        <span className="text-[11px] ml-1 text-[#5C6B64]">({s.interval_days}d)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-[color:var(--color-primary)] tabular-nums"
                        data-testid={`sched-next-due-${s.id}`}>
                      {s.next_due || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-[#5C6B64] tabular-nums">{s.starts_on || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{s.lead_days ?? '—'}</td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">{ROLE_LABEL[s.default_assignee_role] || s.default_assignee_role}</td>
                    <td className="px-4 py-2.5">
                      {s.active ? (
                        <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200">Active</Badge>
                      ) : (
                        <Badge className="bg-[#F1F1EF] text-[#5C6B64] border-[#E2E8DF]">Inactive</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[color:var(--color-primary)]"
                        title="Edit" onClick={() => openEdit(s)} data-testid={`sched-edit-${s.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost"
                        className={`h-7 w-7 p-0 ${s.active ? 'text-[#5C6B64]' : 'text-emerald-600'}`}
                        title={s.active ? 'Deactivate' : 'Activate'}
                        onClick={() => toggleActive(s)} data-testid={`sched-toggle-${s.id}`}>
                        {s.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#C45A41]"
                        title="Delete" onClick={() => remove(s)} data-testid={`sched-del-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <PMScheduleEditorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        assets={assets}
        templatesForAsset={templatesForAsset}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}

function PMScheduleEditorDialog({ open, onOpenChange, editing, form, setForm, assets, templatesForAsset, onSave, saving }) {
  const compatibleTemplates = templatesForAsset(form.asset_id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="sched-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit PM Schedule' : 'Create PM Schedule'}</DialogTitle>
          <DialogDescription>
            The task generator uses these values as the single source of truth. Existing PM tasks
            keep the template snapshot captured at task-generation time.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Asset">
            <Select value={form.asset_id}
                    onValueChange={(v) => setForm((f) => ({
                      ...f, asset_id: v,
                      inspection_template_id: null,
                    }))}
                    disabled={!!editing}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="sched-asset-select">
                <SelectValue placeholder="Select asset" />
              </SelectTrigger>
              <SelectContent>
                {assets.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editing && <div className="text-[10px] text-[#5C6B64] mt-1">Asset cannot be changed after creation.</div>}
          </Field>

          <Field label="Inspection Template">
            <Select value={form.inspection_template_id || 'none'}
                    onValueChange={(v) => setForm((f) => ({
                      ...f, inspection_template_id: v === 'none' ? null : v,
                    }))}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="sched-template-select">
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No template (free-form task) —</SelectItem>
                {compatibleTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Frequency">
            <Select value={form.frequency}
                    onValueChange={(v) => setForm((f) => ({ ...f, frequency: v,
                      interval_days: v === 'custom' ? (f.interval_days || 14) : null,
                    }))}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="sched-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {form.frequency === 'custom' && (
            <Field label="Custom Interval (days)">
              <Input type="number" min={1} value={form.interval_days ?? ''}
                     onChange={(e) => setForm((f) => ({ ...f, interval_days: e.target.value }))}
                     className="h-10 border-[#E2E8DF]" data-testid="sched-interval-input" />
            </Field>
          )}

          <Field label="Start Date">
            <Input type="date" value={form.starts_on || ''}
                   onChange={(e) => setForm((f) => ({ ...f, starts_on: e.target.value }))}
                   className="h-10 border-[#E2E8DF]" data-testid="sched-start-input" />
          </Field>

          <Field label="Lead Days">
            <Input type="number" min={0} value={form.lead_days ?? 3}
                   onChange={(e) => setForm((f) => ({ ...f, lead_days: e.target.value }))}
                   className="h-10 border-[#E2E8DF]" data-testid="sched-lead-input" />
          </Field>

          <Field label="Assignee Role">
            <Select value={form.default_assignee_role}
                    onValueChange={(v) => setForm((f) => ({ ...f, default_assignee_role: v }))}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="sched-assignee-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNEE_ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Description (optional)" full>
            <Input value={form.description}
                   onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                   placeholder="Preventive maintenance"
                   className="h-10 border-[#E2E8DF]" data-testid="sched-desc-input" />
          </Field>

          <div className="sm:col-span-2 flex items-center gap-2 mt-1">
            <input type="checkbox" checked={!!form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              data-testid="sched-active-toggle" id="sched-active" />
            <label htmlFor="sched-active" className="text-[12px] text-[#1A2421] cursor-pointer">
              Active — generate preventive-maintenance tasks from this schedule
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sched-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="sched-save-btn">
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

